
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LoaderCircle, User, Mail, Lock, Eye, EyeOff } from "lucide-react";

const registerSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, "Password must contain at least one uppercase letter, one lowercase letter, and one number"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
});

const roleOptions = [
  { id: "Bartender", label: "Bartender", description: "Manage bar operations and shifts" },
  { id: "DJ", label: "DJ", description: "Perform and manage gigs" },
  { id: "Patron", label: "Patron", description: "Explore and attend events" },
  { id: "Performer", label: "Performer", description: "Showcase performances and manage bookings" },
  { id: "Promoter", label: "Promoter", description: "Organize and manage events" },
  { id: "Security", label: "Security", description: "Manage venue security" },
  { id: "Venue Owner", label: "Venue Owner", description: "Manage venues and bookings" },
] as const;

type RegisterForm = z.infer<typeof registerSchema>;

export const RegisterForm = () => {
  const [step, setStep] = useState<"register" | "roles">("register");
  const [selectedRoles, setSelectedRoles] = useState<string[]>(["Patron"]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
    },
  });

  const cleanupEmailRecords = async (email: string) => {
    try {
      const { error } = await supabase.functions.invoke("delete-user", {
        body: { 
          email_to_clean: email 
        },
      });
      
      if (error) {
        console.error("Error cleaning up email records:", error);
        return false;
      }
      
      console.log("Email records cleaned up successfully:", email);
      return true;
    } catch (error) {
      console.error("Error in cleanup function:", error);
      return false;
    }
  };

  const onSubmitRegister = async (data: RegisterForm) => {
    try {
      setIsLoading(true);
      
      await cleanupEmailRecords(data.email);
      
      const { data: signUpRes, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
          },
        },
      });

      if (signUpError) {
        console.error("Signup error:", signUpError);
        throw signUpError;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error: profileError } = await supabase.from("users").insert({
          id: user.id,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          role_types: ["Patron"],
          onboarding_completed: false,
          user_type: "patron",
          social_links: {},
          streaming_links: {},
          availability: {},
          rate_card: {},
          followers_count: 0,
          following_count: 0,
          portfolio_images: [],
          portfolio_videos: [],
          music_preferences: [],
          event_preferences: [],
          location_preferences: [],
          profile_completion_percentage: 0,
        });

        if (profileError) throw profileError;
      }

      setStep("roles");
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleSelection = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from("users")
        .update({
          role_types: selectedRoles,
          profile_completion_percentage: 20, // Set initial progress percentage
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Success!",
        description: "Now let's finish setting up your account.",
      });

      // Navigate to the account setup wizard instead of dashboard
      navigate("/account-setup");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save role selections.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (step === "roles") {
    return (
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-2 text-center text-white">Tell us about yourself</h2>
        <p className="text-gray-400 mb-6 text-center">
          Which of these best describe you? (Select all that apply)
        </p>
        <div className="space-y-4">
          {roleOptions.map((role) => (
            <div key={role.id} className="flex items-start space-x-3 space-y-0 p-3 border border-gray-800 rounded-lg hover:bg-gray-800/30 transition-colors">
              <div className="flex items-center space-x-3">
                <Switch
                  id={role.id}
                  checked={selectedRoles.includes(role.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedRoles((prev) => 
                        Array.from(new Set([...prev, role.id]))
                      );
                    } else if (role.id !== "Patron") {
                      setSelectedRoles((prev) => 
                        prev.filter((r) => r !== role.id)
                      );
                    }
                  }}
                  disabled={role.id === "Patron"}
                />
                <div className="space-y-1 leading-none">
                  <label
                    htmlFor={role.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {role.label}
                    {role.id === "Patron" && (
                      <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        Default
                      </span>
                    )}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {role.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            disabled={isLoading}
          >
            Skip for now
          </Button>
          <Button 
            onClick={handleRoleSelection} 
            disabled={isLoading}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            {isLoading ? "Saving..." : "Continue"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmitRegister)} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-foreground font-medium">First Name</FormLabel>
                  <FormControl>
                    <div className="relative group">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors duration-200" />
                      <Input 
                        placeholder="John" 
                        className="pl-10 h-12 bg-background/50 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all duration-200 hover:border-border focus:bg-background/80 placeholder:text-muted-foreground/60" 
                        {...field} 
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-destructive text-sm" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-foreground font-medium">Last Name</FormLabel>
                  <FormControl>
                    <div className="relative group">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors duration-200" />
                      <Input 
                        placeholder="Doe" 
                        className="pl-10 h-12 bg-background/50 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all duration-200 hover:border-border focus:bg-background/80 placeholder:text-muted-foreground/60" 
                        {...field} 
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-destructive text-sm" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-foreground font-medium">Email</FormLabel>
                <FormControl>
                  <div className="relative group">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors duration-200" />
                    <Input 
                      type="email" 
                      placeholder="john@example.com" 
                      className="pl-10 h-12 bg-background/50 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all duration-200 hover:border-border focus:bg-background/80 placeholder:text-muted-foreground/60" 
                      {...field} 
                    />
                  </div>
                </FormControl>
                <FormMessage className="text-destructive text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-foreground font-medium">Password</FormLabel>
                <FormControl>
                  <div className="relative group">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors duration-200" />
                    <Input 
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••" 
                      className="pl-10 pr-10 h-12 bg-background/50 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all duration-200 hover:border-border focus:bg-background/80 placeholder:text-muted-foreground/60" 
                      {...field} 
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground transition-colors duration-200"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage className="text-destructive text-sm" />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Password must contain:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li>At least 8 characters</li>
                    <li>One uppercase letter</li>
                    <li>One lowercase letter</li>
                    <li>One number</li>
                  </ul>
                </div>
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-primary/25 transition-all duration-200 font-medium" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
